// The hosted link player (lib/linkPlayer.ts: web_streammapp_com src/player), mounted into our own div — never nested
// in an iframe of ours (YouTube's embed policy). The one place that mounts it: the wall dock and link casts load
// media through the controller it hands over.
import { useEffect, useRef, type CSSProperties } from "react";
import { loadLinkPlayer, type LinkPlayerApi, type LinkPlayerController, type LinkPlayerEvent } from "../lib/linkPlayer";

export interface EmbedPlayerProps {
  /** Mounted: load media through `controller`; `api.CONTROLS` says whether load() takes `controls`. */
  onMount(controller: LinkPlayerController, api: LinkPlayerApi): void;
  onEvent(e: LinkPlayerEvent): void;
  /** The player script failed to load (nothing can play). */
  onScriptError(): void;
  /** After the controller was destroyed (unmount): drop what was kept of it. */
  onDestroy?(): void;
  testId?: string;
  className?: string;
  style?: CSSProperties;
}

/** Mounts once per component instance (key it to start over); destroyed on unmount, also when the script is late. */
export default function EmbedPlayer({ testId, className, style, ...cbs }: EmbedPlayerProps) {
  const host = useRef<HTMLDivElement>(null);
  const cb = useRef(cbs);
  cb.current = cbs;
  useEffect(() => {
    let alive = true;
    let controller: LinkPlayerController | null = null;
    loadLinkPlayer().then(
      (api) => {
        if (!alive || !host.current) return;
        controller = api.mount(host.current, (e) => cb.current.onEvent(e));
        cb.current.onMount(controller, api);
      },
      () => {
        if (alive) cb.current.onScriptError();
      },
    );
    return () => {
      alive = false;
      controller?.destroy();
      cb.current.onDestroy?.();
    };
  }, []);
  return <div ref={host} data-testid={testId} className={className} style={style} />;
}
